import getCart from './getCart';

const FOR_PRODUCT_KEY = '_for_product';

let state = {
  parentProductId: null,
  variantId: null,
  variantTitle: null,
  variantPriceFormatted: null,
  variantIds: [],
  inCart: false,
  lineItemKey: null,
  isEditing: false,
};

const storageKey = parentProductId => `warranty-selection-${parentProductId}`;

export const getWarrantySelection = () => ({ ...state });

export const setWarrantySelection = updates => {
  state = { ...state, ...updates };
  if (state.parentProductId && state.variantId && !state.inCart) {
    try {
      sessionStorage.setItem(
        storageKey(state.parentProductId),
        JSON.stringify({
          variantId: state.variantId,
          variantTitle: state.variantTitle,
          variantPriceFormatted: state.variantPriceFormatted,
        })
      );
    } catch (error) {
      console.error('Failed to persist warranty selection to sessionStorage:', error);
    }
  }
  document.dispatchEvent(new CustomEvent('warranty:change', { detail: getWarrantySelection() }));
};

export const clearWarrantySelection = ({ clearStorage = true } = {}) => {
  const parentProductId = state.parentProductId;
  state = {
    ...state,
    variantId: null,
    variantTitle: null,
    variantPriceFormatted: null,
    inCart: false,
    lineItemKey: null,
    isEditing: false,
  };
  if (clearStorage && parentProductId) {
    try {
      sessionStorage.removeItem(storageKey(parentProductId));
    } catch (error) {
      console.error('Failed to clear warranty selection from sessionStorage:', error);
    }
  }
  document.dispatchEvent(new CustomEvent('warranty:change', { detail: getWarrantySelection() }));
};

const readStoredWarrantySelection = parentProductId => {
  try {
    const stored = sessionStorage.getItem(storageKey(parentProductId));
    if (!stored) return null;
    const parsed = JSON.parse(stored);
    const variantId = Number(parsed?.variantId);
    if (!variantId) return null;
    return {
      variantId,
      variantTitle: parsed.variantTitle,
      variantPriceFormatted: parsed.variantPriceFormatted,
    };
  } catch (error) {
    console.error('Failed to read warranty selection from sessionStorage:', error);
    return null;
  }
};

export const hasStagedWarrantyForProduct = parentProductId => {
  const parentId = Number(parentProductId);
  if (!parentId) return false;

  const selection = getWarrantySelection();
  const selectionParentId = Number(selection.parentProductId) || parentId;

  if (selection.variantId && !selection.inCart && selectionParentId === parentId) {
    return true;
  }

  return Boolean(readStoredWarrantySelection(parentId)?.variantId);
};

export const initWarrantySelection = ({ parentProductId, variantIds }) => {
  state.parentProductId = parentProductId;
  state.variantIds = variantIds;

  const stored = readStoredWarrantySelection(parentProductId);
  if (!stored) return;

  const isValidVariant = variantIds.some(id => Number(id) === stored.variantId);
  if (!isValidVariant) return;

  state.variantId = stored.variantId;
  state.variantTitle = stored.variantTitle;
  state.variantPriceFormatted = stored.variantPriceFormatted;
};

export const getLineItemProperty = (item, key) => {
  const props = item.properties;
  if (!props) return null;
  if (Array.isArray(props)) {
    const entry = props.find(property => property?.key === key || property?.name === key);
    return entry?.value ?? null;
  }
  if (typeof props === 'object') {
    return props[key] ?? null;
  }
  return null;
};

export const findWarrantyLineItem = (cart, { parentProductId, variantIds }) => {
  if (!cart?.items?.length || !variantIds?.length) return null;

  const parentId = String(parentProductId);
  const ids = variantIds.map(id => Number(id));
  const warrantyCandidates = cart.items.filter(item => ids.includes(Number(item.variant_id)));

  if (!warrantyCandidates.length) return null;

  const matchedByProperty = warrantyCandidates.find(item => {
    const propertyValue = getLineItemProperty(item, FOR_PRODUCT_KEY);
    return propertyValue != null && String(propertyValue) === parentId;
  });
  if (matchedByProperty) return matchedByProperty;

  if (warrantyCandidates.length === 1) {
    return warrantyCandidates[0];
  }

  return (
    warrantyCandidates.find(item => {
      const propertyValue = getLineItemProperty(item, FOR_PRODUCT_KEY);
      return propertyValue == null || String(propertyValue) === parentId;
    }) || null
  );
};

export const getWarrantyCartProperties = parentProductId => ({
  [FOR_PRODUCT_KEY]: String(parentProductId),
});

export const getWarrantyQuantity = () => {
  const qtyInput = document.querySelector('[data-product-form] [name="quantity"]');
  return qtyInput ? Math.max(1, Number(qtyInput.value) || 1) : 1;
};

export const appendStagedWarrantyToItems = (items, quantity = getWarrantyQuantity()) => {
  const root = document.querySelector('[data-product-warranty]');
  if (!root) return { items, added: false, parentProductId: null };

  const parentProductId = Number(root.dataset.parentProductId);
  if (!parentProductId || !hasStagedWarrantyForProduct(parentProductId)) {
    return { items, added: false, parentProductId };
  }

  const selection = getWarrantySelection();
  let variantId = selection.inCart ? null : Number(selection.variantId);

  if (!variantId) {
    variantId = readStoredWarrantySelection(parentProductId)?.variantId ?? null;
  }

  if (!variantId) {
    return { items, added: false, parentProductId };
  }

  if (!selection.variantId || Number(selection.parentProductId) !== parentProductId) {
    const stored = readStoredWarrantySelection(parentProductId);
    setWarrantySelection({
      parentProductId,
      variantId,
      variantTitle: stored?.variantTitle ?? selection.variantTitle,
      variantPriceFormatted: stored?.variantPriceFormatted ?? selection.variantPriceFormatted,
      inCart: false,
      isEditing: false,
    });
  }

  return {
    items: [
      ...items,
      {
        id: variantId,
        quantity,
        properties: getWarrantyCartProperties(parentProductId),
      },
    ],
    added: true,
    parentProductId,
  };
};

export const markWarrantyInCartFromCart = async (parentProductId, { variantIds } = {}) => {
  const selection = getWarrantySelection();
  let ids = variantIds;
  if (!ids?.length && selection.variantIds?.length) {
    ids = selection.variantIds;
  }
  if (!ids?.length) {
    ids = [selection.variantId].filter(Boolean);
  }

  const cart = await getCart();
  const lineItem = findWarrantyLineItem(cart, {
    parentProductId,
    variantIds: ids,
  });

  if (!lineItem) {
    console.warn('Warranty line item was not found in cart after add');
    return false;
  }

  setWarrantySelection({
    inCart: true,
    isEditing: false,
    lineItemKey: lineItem.key,
  });
  return true;
};

export const persistWarrantySelectionForProduct = (parentProductId, data) => {
  if (!parentProductId || !data?.variantId) return;
  try {
    sessionStorage.setItem(
      storageKey(parentProductId),
      JSON.stringify({
        variantId: data.variantId,
        variantTitle: data.variantTitle,
        variantPriceFormatted: data.variantPriceFormatted,
      })
    );
  } catch (error) {
    console.error('Failed to persist warranty selection for product to sessionStorage:', error);
  }
};

export const clearWarrantyStorageForProduct = parentProductId => {
  if (!parentProductId) return;
  try {
    sessionStorage.removeItem(storageKey(parentProductId));
  } catch (error) {
    console.error('Failed to clear warranty storage for product from sessionStorage:', error);
  }
};

export const FOR_PRODUCT_PROPERTY = FOR_PRODUCT_KEY;

export default {
  getWarrantySelection,
  setWarrantySelection,
  clearWarrantySelection,
  initWarrantySelection,
  hasStagedWarrantyForProduct,
  findWarrantyLineItem,
  getWarrantyCartProperties,
  persistWarrantySelectionForProduct,
  clearWarrantyStorageForProduct,
};
